import { createAction, handleActions } from 'redux-actions';

export type ModulesIds = 'analytics' | 'data_activation' | 'tag_manager' | 'consent_manager' | 'administration';
type ModulesUrls = '/analytics' | '/cdp' | '/tag' | '/consent';
export type LicensePlanId = 'trial' | 'business' | 'enterprise_legacy' | 'enterprise_data_fundamentals' | 'enterprise_trusted_insights' | 'enterprise_secure_intelligence';
export type LicenseStatus = 'active' | 'inactive' | 'expired' | 'upcoming';

export interface PpasModule {
  id: ModulesIds
  href: ModulesUrls
}

/* eslint-disable camelcase */
export interface LicenseAttributes {
  status: LicenseStatus
  active_at: string
  expires_at: string | null
  inactive_at: string
  updated_at: string
  plan: {
    id: LicensePlanId
    name: string
    administration: {
      data_retention_limit: number
      traffic_limit: number
      apps_limit: number
      sso_available: boolean
      ci_domains_limit: number
      meta_sites_limit: number
      audit_log_retention_limit: number
    }
  } & Partial<Record<ModulesIds, Record<string, string | number | boolean>>>
  modules: ModulesIds[]
}

export interface Capguard {
  is_excluded: boolean
}

export interface OrganizationDetailsResponse {
  license: {
    current: LicenseAttributes
    upcoming?: LicenseAttributes
  }
  'data_retention': {
    'start_date': string | null
  }
  capguard: Capguard
}

export type DetailsState = {
  isLoading: true
  failed: false
  license: {
    current: null
    upcoming: null
  }
  dataRetention: null
  capguard: null
} | {
  isLoading: false
  failed: false
  license: {
    current: LicenseAttributes
    upcoming: LicenseAttributes | null
  }
  dataRetention: OrganizationDetailsResponse['data_retention']
  capguard: Capguard
} | {
  isLoading: false
  failed: true
  license: {
    current: null
    upcoming: null
  }
  dataRetention: null
  capguard: null
};

export const fetchOrganizationDetailsInitialized = createAction(
  '@platform/query/organization/getDetails/init',
);
export const fetchOrganizationDetailsSucceeded = createAction(
  '@platform/query/organization/getDetails/success',
  (payload: OrganizationDetailsResponse) => payload,
);
export const fetchOrganizationDetailsFailed = createAction(
  '@platform/query/organization/getDetails/failed',
);

const initialState: DetailsState = {
  isLoading: true,
  failed: false,
  license: {
    current: null,
    upcoming: null,
  },
  dataRetention: null,
  capguard: null,
};

export default handleActions<DetailsState, OrganizationDetailsResponse>({
  [fetchOrganizationDetailsInitialized.toString()]: state => ({
    ...state,
    isLoading: true,
    failed: false,
    license: {
      current: null,
      upcoming: null,
    },
    dataRetention: null,
    capguard: null,
  }),
  [fetchOrganizationDetailsSucceeded.toString()]: (state, { payload }) => ({
    ...state,
    isLoading: false,
    failed: false,
    license: {
      current: payload.license.current,
      upcoming: payload.license.upcoming ?? null,
    },
    dataRetention: payload.data_retention,
    capguard: payload.capguard,
  }),
  [fetchOrganizationDetailsFailed.toString()]: state => ({
    ...state,
    isLoading: false,
    failed: true,
    license: {
      current: null,
      upcoming: null,
    },
    dataRetention: null,
    capguard: null,
  }),
}, initialState);
